import React from 'react';
import {
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Button } from 'components/Button';

const EARLIEST_YEAR = 2025;

interface StageOption {
  value: string;
  label: string;
}

interface JudgmentFiltersProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filterCategory: string;
  setFilterCategory: (category: string) => void;
  categories: string[];
  handleRefresh: () => void;
  loading: boolean;
  userPillar?: string | null;
  userIsSuperAdmin?: boolean;
  year: number;
  setYear: React.Dispatch<React.SetStateAction<number>>;
  filterStage?: string;
  setFilterStage?: (stage: string) => void;
  allStages?: StageOption[];
}

const TypedButton = Button as any;

const JudgmentFilters: React.FC<JudgmentFiltersProps> = ({
  searchQuery,
  setSearchQuery,
  filterCategory,
  setFilterCategory,
  categories,
  handleRefresh,
  loading,
  userPillar,
  userIsSuperAdmin,
  year,
  setYear,
  filterStage = '',
  setFilterStage,
  allStages = [],
}) => {
  const currentYear = new Date().getFullYear();

  const handlePrevYear = () => {
    if (year > EARLIEST_YEAR) setYear(y => y - 1);
  };

  const handleNextYear = () => {
    if (year < currentYear) setYear(y => y + 1);
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
      {/* Year selector bar */}
      <div className="flex items-center justify-between gap-4 px-4 py-3 bg-gradient-to-r from-gray-900 to-gray-700 border-b border-gray-600">
        <div className="flex items-center gap-2 text-white">
          <CalendarDaysIcon className="h-4 w-4 opacity-70" />
          <span className="text-xs font-baijamjuree uppercase tracking-widest opacity-70">
            Award Year
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevYear}
            disabled={year <= EARLIEST_YEAR}
            aria-label="Previous year"
            className="p-1 rounded-full text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-25 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>

          {/* Year chips */}
          <div className="flex gap-1">
            {Array.from(
              { length: currentYear - EARLIEST_YEAR + 1 },
              (_, i) => EARLIEST_YEAR + i
            ).map(y => (
              <button
                key={y}
                onClick={() => setYear(y)}
                className={`px-3 py-1 rounded-full text-sm font-baijamjuree font-bold transition-all duration-200 ${
                  y === year
                    ? 'bg-white text-gray-900 shadow-md scale-105'
                    : 'text-white/70 hover:text-white hover:bg-white/15'
                }`}
              >
                {y}
              </button>
            ))}
          </div>

          <button
            onClick={handleNextYear}
            disabled={year >= currentYear}
            aria-label="Next year"
            className="p-1 rounded-full text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-25 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Active year badge */}
        <div className="flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs font-baijamjuree text-white font-semibold">
            Showing {year}
          </span>
        </div>
      </div>

      {/* Filters row */}
      <div className="p-4">
        {(userPillar || userIsSuperAdmin) && (
          <div
            className={`mb-4 rounded-lg p-3 ${
              userIsSuperAdmin
                ? 'bg-purple-50 border border-purple-200'
                : 'bg-blue-50 border border-blue-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <div
                className={`w-3 h-3 rounded-full ${
                  userIsSuperAdmin ? 'bg-purple-500' : 'bg-blue-500'
                }`}
              />
              <span
                className={`text-sm font-baijamjuree font-medium ${
                  userIsSuperAdmin ? 'text-purple-800' : 'text-blue-800'
                }`}
              >
                {userIsSuperAdmin ? (
                  <>
                    <span className="font-bold">🔑 Super Admin Access</span> —
                    Viewing ALL submissions from all pillars
                  </>
                ) : (
                  <>
                    Viewing submissions for:{' '}
                    <span className="font-bold">
                      {userPillar === 'Others' ? 'Initiative' : userPillar}
                    </span>
                  </>
                )}
              </span>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 items-center">
          {/* Search Input */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search submissions..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 bg-white font-baijamjuree text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>

          {/* Category Filter */}
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-baijamjuree text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          {/* Stage Filter */}
          {allStages.length > 0 && (
            <select
              value={filterStage}
              onChange={e => setFilterStage(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-baijamjuree text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="">All Stages</option>
              {allStages.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          )}

          {/* Refresh Button */}
          <TypedButton
            onClick={handleRefresh}
            disabled={loading}
            className="bg-black hover:bg-gray-800 text-white rounded-full px-6 py-3 font-baijamjuree font-bold border border-black shadow-xl transition-colors disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </TypedButton>
        </div>
      </div>
    </div>
  );
};

export default JudgmentFilters;
